// helpers
import { isEmpty } from "@swiggy-private/checkout-core";
import { validatePriceAndPrepareForRender, slaTimerStringBuilder } from "@cart-client/helpers/GiftingCart";
import { getUniqueIdForCartItem } from "@cart-client/helpers/CartItemRenderer";
// types
import {
    CombinedCartInStockItemRender,
    GiftingCartApiResponse,
    IGiftingInternalBusinessLine,
    IMFilteredStructureItem,
    IMServiceabilityStatus,
    IMShipmentDetails,
    IMStructureItem,
    ServiceLine,
    IMGiftBagItem,
    IMGiftBagItemWithUUID,
    GiftingCartItemType,
} from "@swiggy-private/cart-types";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { Item, CTAType } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard";
import { STATIC_TEXTS, CTATypeToText } from "@cart-client/constants/GiftingCart";

export const isIMSubCart = (subCart: CombinedCartInStockItemRender) => {
    return subCart.businessLine === IGiftingInternalBusinessLine.INSTAMART;
};

export const isIMGiftBagItem = (subType?: GiftingCartItemType) => {
    return subType === GiftingCartItemType.INSTAMART_GIFT_BAG;
};

export const isBasicIMItem = (subType?: GiftingCartItemType) => {
    return subType === GiftingCartItemType.INSTAMART;
};

export const isAnyIMItem = (subType?: GiftingCartItemType) => {
    return isBasicIMItem(subType) || isIMGiftBagItem(subType);
};

export const getIMCouponlessSavingWidgetData = (
    cartResponse: GiftingReviewedCart | null,
): GiftingCartApiResponse["im_couponless_savings_widget_info"] => {
    return cartResponse?.data?.im_couponless_savings_widget_info;
};

export const getIMCartItemsForUpdateCartAPI = (
    imCartItems?: IMStructureItem[],
): IMFilteredStructureItem[] | undefined => {
    if (isEmpty(imCartItems)) {
        return;
    }
    const filteredItems: IMFilteredStructureItem[] = [];
    imCartItems?.forEach((item: IMStructureItem) => {
        if (item.quantity < 1) {
            return;
        }
        const {
            type,
            itemId,
            metadata = null,
            freebie = false,
            storeId,
            isGiftBag = false,
            itemType,
            quantity,
            spin,
            productId,
        } = item;
        filteredItems.push({
            quantity,
            itemId,
            spin: metadata?.variations?.[0]?.spin || spin || "",
            productId: metadata?.product_id || productId || "",
            storeId: String(storeId || ""),
            meta: {
                type,
                freebie,
                isGiftBag,
                ...(itemType && { itemType }),
            },
            serviceLine: ServiceLine.INSTAMART,
        });
    });

    return filteredItems;
};

export const getIMGiftBagItemsForUpdateCartAPI = (
    imGiftBagItems?: IMGiftBagItem[],
): IMFilteredStructureItem[] | undefined => {
    if (isEmpty(imGiftBagItems)) {
        return;
    }
    const filteredItems: IMFilteredStructureItem[] = [];
    imGiftBagItems?.forEach((item: IMGiftBagItem) => {
        filteredItems.push({
            // hardcoded quantity to 1 for gift bag
            quantity: 1,
            itemId: item?.variations?.[0]?.id || "",
            spin: item?.variations?.[0]?.spin || "",
            productId: item?.product_id || "",
            storeId: String(item?.variations?.[0]?.store_id || ""),
            meta: {
                // hardcoded type to structure for gift bag
                type: "structure",
                freebie: false,
                isGiftBag: true,
            },
            serviceLine: ServiceLine.INSTAMART,
        });
    });
    return filteredItems;
};

export const getIMItemDescription = (item: IMStructureItem) => {
    if (!isEmpty(item?.metadata?.variations)) {
        const variant = item?.metadata?.variations[0];
        return variant?.sku_quantity_with_combo || variant?.quantity || "";
    }
    return "";
};

export const getIMItemFinalPrice = (item: IMStructureItem) => {
    if (!isEmpty(item?.metadata?.variations)) {
        const variant = item?.metadata?.variations[0];
        const priceFromVariant = variant?.price?.offer_price || variant?.price?.store_price;
        const price = priceFromVariant ? priceFromVariant * item?.quantity : 0;
        return validatePriceAndPrepareForRender(price);
    }
    return "";
};

const getIMItemBasePrice = (item: IMStructureItem) => {
    if (!isEmpty(item?.metadata?.variations)) {
        const variant = item?.metadata?.variations[0];
        const priceFromVariant = variant?.price?.mrp;
        const price = priceFromVariant ? priceFromVariant * item?.quantity : 0;
        return validatePriceAndPrepareForRender(price);
    }
    return "";
};

/**
 * Build props for IM items and Gift Bags added to cart
 */
export const buildIMItemToRenderProps = ({
    parentCartId,
    items,
    onItemAction,
    combineRestrictedAndAvailableItems = false,
    impressionCallback,
}: {
    parentCartId?: string;
    items?: IMStructureItem[];
    onItemAction?: (item: Item, ctaType: CTAType) => void;
    combineRestrictedAndAvailableItems?: boolean;
    impressionCallback?: (item: Item) => void;
}) => {
    if (isEmpty(items)) {
        return null;
    }
    const nonRestrictedQuantityItems: Item[] = [];
    const restrictedQuantityItems: Item[] = [];

    items?.map((item) => {
        const quantity = item?.quantity ?? 0;
        let baseCTAType = CTAType.QUANTITY_INC_DEC;
        const isGiftBagItem = (item?.isGiftBag || item?.metadata?.gift_bag_item) ?? false;
        if (quantity > 0 && isGiftBagItem) {
            baseCTAType = CTAType.REMOVE;
        } else if (quantity === 0 && isGiftBagItem) {
            baseCTAType = CTAType.ADD;
        }

        const itemPayload = {
            parentCartId,
            id: item?.itemId ?? "",
            title: item?.name ?? "",
            subtitle: getIMItemDescription(item),
            quantity: quantity,
            imageId: item?.imageIds?.[0] ?? "",
            finalPrice: getIMItemFinalPrice(item),
            basePrice: getIMItemBasePrice(item),
            ctaType: item?.outOfStock ? CTAType.OOS : baseCTAType,
            ctaText: item?.outOfStock ? STATIC_TEXTS.OOS : CTATypeToText[baseCTAType],
            // TODO: Add logic to handle updating state
            updating: false,
            subType: isGiftBagItem ? GiftingCartItemType.INSTAMART_GIFT_BAG : GiftingCartItemType.INSTAMART,
            unavailableText: item.outOfStock ? STATIC_TEXTS.OOS : "",
            ctaClick: onItemAction,
            impressionEventCallback: impressionCallback,
        } as Item;

        if (item?.stockLimited && !combineRestrictedAndAvailableItems) {
            restrictedQuantityItems.push(itemPayload);
        } else {
            nonRestrictedQuantityItems.push(itemPayload);
        }
    });
    return {
        items: nonRestrictedQuantityItems,
        restrictedQuantityItems,
    };
};

/**
 * Build props for Gift Bags to be added to cart
 */
export const buildIMGiftBagItemToRenderProps = ({
    parentCartId,
    items,
    title,
    description,
    creativeId,
    onItemAction,
    impressionCallback,
}: {
    parentCartId?: string;
    items?: IMGiftBagItem[];
    title?: string;
    description?: string;
    creativeId?: string;
    onItemAction?: (item: Item, ctaType: CTAType) => void;
    impressionCallback?: (item: Item) => void;
}) => {
    if (isEmpty(items)) {
        return null;
    }
    const filteredIMGiftBagItems = items?.map((item) => {
        return {
            parentCartId,
            id: item?.variations?.[0]?.id || "",
            title: title || "",
            subtitle: description || "",
            imageId: creativeId || "",
            quantity: 0,
            finalPrice: validatePriceAndPrepareForRender(
                item?.variations?.[0]?.price?.offer_price || item?.variations?.[0]?.price?.store_price,
            ),
            basePrice: validatePriceAndPrepareForRender(item?.variations?.[0]?.price?.mrp),
            ctaType: CTAType.ADD,
            ctaText: CTATypeToText[CTAType.ADD],
            // TODO: Add logic to handle updating state
            updating: false,
            subType: GiftingCartItemType.INSTAMART_GIFT_BAG,
            unavailableText: "",
            ctaClick: onItemAction,
            impressionEventCallback: impressionCallback,
        } as Item;
    });
    return filteredIMGiftBagItems || [];
};

export const doesItemBelongToShipment = (item: IMStructureItem, shipment: IMShipmentDetails) => {
    return String(item.shipmentIdV2) === String(shipment.id) || String(item.storeId) === String(shipment.storeId);
};

export const doesGiftBagItemBelongToShipmentOrStore = (item: IMGiftBagItem, shipment: IMShipmentDetails) => {
    return String(item.variations?.[0]?.store_id) === String(shipment.storeId);
};

export const isIMShipmentServiceable = (shipment: IMShipmentDetails) => {
    return shipment.serviceabilityDetails.serviceabilityStatus === IMServiceabilityStatus.SERVICEABLE;
};

export const segregateIMItemByShipmentId = ({
    items,
    shipments,
    giftBagItems,
}: {
    items?: IMStructureItem[];
    shipments?: IMShipmentDetails[];
    giftBagItems?: IMGiftBagItem[];
}): CombinedCartInStockItemRender[] => {
    if ((isEmpty(items) && isEmpty(giftBagItems)) || isEmpty(shipments)) {
        return [];
    }
    const imItemsDividedIntoShipments: CombinedCartInStockItemRender[] = [];
    shipments?.forEach((shipment, index) => {
        const itemList = items?.filter((item) => doesItemBelongToShipment(item, shipment));
        const giftBagItemList = giftBagItems?.filter((item) => doesGiftBagItemBelongToShipmentOrStore(item, shipment));
        // some items should be present for a shipment then only the subcart + gift bag for that shipment should be added
        if (!isEmpty(itemList)) {
            imItemsDividedIntoShipments.push({
                subCartId: `${IGiftingInternalBusinessLine.INSTAMART}_${index}_${Date.now()}`,
                isServiceable: isIMShipmentServiceable(shipment),
                slaTime: shipment.serviceabilityDetails.slaTime,
                slaTimeString: slaTimerStringBuilder(shipment.serviceabilityDetails.slaTime),
                items: itemList,
                giftBagItems: giftBagItemList,
                businessLine: IGiftingInternalBusinessLine.INSTAMART,
                slaTagImage: shipment?.icon,
                disclaimer: shipment?.disclaimer || "",
            });
        }
    });
    return imItemsDividedIntoShipments;
};

export const createGiftingGiftBagItemListWithUUID = (cartItems: IMGiftBagItem[]): IMGiftBagItemWithUUID[] => {
    if (isEmpty(cartItems)) {
        return [];
    }
    const list: IMGiftBagItemWithUUID[] = [];
    // if no items return empty list
    if (cartItems.length < 1) {
        return list;
    }
    cartItems.forEach((item: IMGiftBagItem, index: number) => {
        list.push({
            ...item,
            unId: getUniqueIdForCartItem(String(item?.variations?.[0]?.id), index),
            itemPosition: index,
        });
    });
    return list;
};
